import { useId, useRef, useState } from 'react';
import { Pencil } from 'lucide-react';

import { currencySymbol, formatQuoteDigits } from '@/lib/format';

/**
 * What this vendor costs, and who said so.
 *
 * It fills itself again. The newest extraction the planner has not struck out
 * feeds this figure — and when the vendor priced several packages it feeds a
 * span, `2,400 – 4,800` beside the currency symbol, because a three-tier
 * proposal has no single price and naming one would be a choice nobody made.
 * That choice was the objection behind `d8d2298`, which answered it by cutting
 * resolution out of the field altogether and leaving the planner to retype
 * what the extractor had already read. The span is the cheaper answer: it
 * needs no choice made on anyone's behalf to be true.
 *
 * A figure that writes itself has to be reconstructible, which is what the
 * caption beneath a span is for — it names how many packages are standing
 * behind the two numbers. The tree itself is one tab away.
 *
 * The planner's own figure still ends the question: typing over this pins it,
 * and nothing resolves over a number they entered. That is the whole override
 * surface — no reject button, no undo. A span therefore has no clearing
 * gesture *from the empty field it focuses into*, which is deliberate: there
 * is nothing there to delete, and disagreeing with a span means typing the
 * figure you mean. Typing and then deleting does clear, for a span exactly as
 * for a single figure — and a clear is a rejection, not a blank: the server
 * strikes out the reading behind the figure, one layer at a time, and no route
 * puts it back.
 *
 * It is drawn as a value in the page rather than as a form control: a box at
 * `text-2xl` competes with the vendor's name for the largest object on the
 * header, and the quote is a fact about this inquiry, not a question being
 * asked.
 */

export interface ResolvedQuote {
  amount: number;
  /** The dear end of a package range; null when the headline is one figure,
   *  which `amount` always holds. */
  amountHigh: number | null;
  currency: string;
}

export default function QuoteField({
  quote,
  packageCount,
  saving,
  onChange,
}: {
  quote: ResolvedQuote | null;
  /** How many packages the vendor put on the table, which is what the caption
   *  under a span reports. Required rather than optional: a span with no
   *  account of itself is the figure-from-nowhere `d8d2298` objected to, and
   *  every caller has this number. 0 where there is no tree. */
  packageCount: number;
  saving: boolean;
  /** Null clears the field, which the server reads as a rejection: it strikes
   *  out the reading behind the figure, one layer at a time. */
  onChange: (amount: number | null) => void;
}) {
  /**
   * The keystrokes, while there are any. Null means "show the saved figure",
   * which is what makes an external change — another tab, a failed save
   * rolling back — visible without fighting the planner mid-edit.
   */
  const [draft, setDraft] = useState<string | null>(null);
  /** Focused on a range, which shows an empty field. Separate from `draft`
   *  because an untouched empty field means "no change" while a *typed* empty
   *  one means "clear" — and for a range those look identical. */
  const [pinning, setPinning] = useState(false);

  /**
   * Whether a keystroke happened, and whether the edit is being abandoned.
   *
   * Recorded rather than inferred, which is the point. Reading "did the
   * planner type?" off the draft's *value* cannot tell an untouched empty
   * field from an emptied one, and cannot see a refetch — this query runs at
   * `staleTime: 0` and refetches on window focus — replacing the saved figure
   * under a draft nobody touched.
   *
   * Refs, not state, because the blur that reads them is dispatched *inside*
   * the handler that sets them: `e.currentTarget.blur()` fires `focusout`
   * synchronously, before React lands the pending updates, so that `onBlur`
   * belongs to the last committed render. A state flag reads `false` there
   * every time, and Escape would commit the draft it was abandoning.
   */
  const typed = useRef(false);
  const abandoning = useRef(false);

  const fieldId = useId();
  const captionId = useId();

  const ranged = quote?.amountHigh != null;
  const saved = quote ? formatQuoteDigits(quote.amount, quote.amountHigh) : '';
  const value = pinning && draft === null ? '' : (draft ?? saved);
  const placeholder = ranged ? 'Pin' : 'Add';
  /** The one figure on screen, or null when what is on screen is a span. */
  const showing = quote && !ranged ? quote.amount : null;
  const counted = ranged && packageCount > 1;

  function commit(edited: boolean) {
    if (draft === null) return;
    const entry = draft.trim();
    setDraft(null);

    // Focusing and leaving is not an edit, and this is what lets a range focus
    // into an empty field at all: without it, clicking in and clicking away
    // would read as deleting the figure, and a clear cannot be taken back. It
    // covers the untouched *pre-filled* draft too — a refetch that turns a
    // single figure into a span mid-edit must not make the planner's idle
    // caret pin the cheapest tier.
    if (!edited) return;

    if (entry === '') {
      if (quote) onChange(null);
      return;
    }

    // Commas and a currency symbol are what a planner pastes out of an email,
    // and refusing them would be refusing the most ordinary way this field
    // gets filled.
    const parsed = Number(entry.replace(/[^0-9.]/g, ''));
    if (!Number.isFinite(parsed) || parsed < 0) return;
    // A request-saving check, now that `edited` is what decides whether
    // anything happened: retyping the figure already on screen writes nothing.
    // It compares against `showing`, which is null for a span — there
    // `quote.amount` is the cheap end, and typing that very number is how a
    // planner pins the cheapest tier.
    if (parsed === showing) return;
    onChange(parsed);
  }

  return (
    <div className="space-y-1">
      <label htmlFor={fieldId} className="label-caps block text-[0.6875rem] text-muted-foreground">
        Quote
      </label>

      {/* The figure sits in the page rather than in a box.

          A bordered input made the quote look like a form to fill in, when it
          is a fact about this vendor that happens to be editable — and at
          `text-2xl` the box was the largest object on the header, competing
          with the vendor's own name.

          What the box was buying was the signal that this can be typed in, so
          that is kept and the rest is dropped: a dotted rule under the digits,
          which is the convention for an editable value everywhere else it
          appears, and a pencil beside them. Both firm up on focus, so the
          field confirms it took the caret. */}
      <div className="group inline-flex items-baseline gap-1">
        <span
          aria-hidden
          className={`display-tight text-2xl ${quote ? 'text-foreground' : 'text-muted-foreground/60'}`}
        >
          {currencySymbol(quote?.currency ?? 'USD')}
        </span>

        {/* The box is as wide as the text in it, measured by invisible twins
            of that text rather than counted in `ch`.

            `ch` is the advance of a zero, and in this serif the comma, the
            spaces and the en dash of `2,400 – 4,800` are each about half of
            one — so a character count left a finger's width of dead air
            between the digits and the pencil, which is the gap sizing the box
            at all exists to close.

            Two twins, not one, because a grid column takes the widest item in
            the cell: one measures what is in the field, the other the figure
            that is saved. The box is therefore never narrower than the quote
            it belongs to, so clicking into a range and typing the first digit
            does not snap the pencil leftwards and then walk it back out one
            keystroke at a time. It grows only when a draft outgrows the saved
            figure. `size={1}` keeps the input's own intrinsic width out of the
            measurement, and the trailing space is the caret's room.

            The floor is about what `Add` occupies, which is the one state no
            twin can hold open: a quote-less field has nothing saved behind it,
            and without the floor the first digit typed into one would shrink
            the rule rather than extend it. */}
        <span className="inline-grid min-w-[2rem]">
          <span
            aria-hidden
            /* A transparent rule of the input's own weight: same box, so the
               row's baseline is the same wherever it is taken from. */
            className={`display-tight invisible col-start-1 row-start-1 whitespace-pre
              border-b-2 border-transparent pb-0.5 tabular-nums
              ${value ? 'text-2xl' : 'text-[1.125rem]'}`}
          >
            {/* The placeholder is rendered a size down by the input, so it is
                measured a size down too. */}
            {`${value || placeholder} `}
          </span>
          <span
            aria-hidden
            className="display-tight invisible col-start-1 row-start-1 whitespace-pre
              border-b-2 border-transparent pb-0.5 text-2xl tabular-nums"
          >
            {`${saved} `}
          </span>
          <input
            id={fieldId}
            size={1}
            inputMode="decimal"
            autoComplete="off"
            disabled={saving}
            value={value}
            /* `Pin` only while a span is showing: it names what the keystrokes
               would do, which for a range is choose rather than add. */
            placeholder={placeholder}
            aria-describedby={counted ? captionId : undefined}
            onFocus={() => {
              typed.current = false;
              abandoning.current = false;
              // A range cannot pre-fill — the low end sitting in the box would
              // become a pinned figure on any blur at all. Everything else
              // does, so correcting a typo in your own number stays one
              // keystroke.
              if (ranged) setPinning(true);
              else setDraft(quote ? String(quote.amount) : '');
            }}
            onChange={(e) => {
              typed.current = true;
              setDraft(e.target.value);
            }}
            onBlur={() => {
              const edited = typed.current;
              typed.current = false;
              setPinning(false);
              if (abandoning.current) {
                abandoning.current = false;
                setDraft(null);
                return;
              }
              commit(edited);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') e.currentTarget.blur();
              if (e.key === 'Escape') {
                abandoning.current = true;
                setDraft(null);
                setPinning(false);
                e.currentTarget.blur();
              }
            }}
            /* Tabular figures so a corrected amount does not shuffle the
               digits sideways as it changes. */
            className="display-tight col-start-1 row-start-1 w-full border-b-2 border-dotted
              border-muted-foreground/40 bg-transparent pb-0.5 text-2xl tabular-nums
              text-foreground outline-none transition-colors
              placeholder:text-[1.125rem] placeholder:text-muted-foreground/60
              group-hover:border-muted-foreground/70
              focus:border-solid focus:border-primary
              disabled:cursor-not-allowed disabled:opacity-60"
          />
        </span>

        <Pencil
          aria-hidden
          className="size-3 self-center text-muted-foreground/50 transition-colors
            group-hover:text-muted-foreground group-focus-within:text-primary"
        />
      </div>

      {/* Why there are two numbers. Without it the span is a figure that
          appeared on its own, which is the thing `d8d2298` objected to.

          No singular: a span exists only where two packages priced out
          differently, and `packageCount` counts every package the vendor
          offered — so it is 2 or more whenever this renders, and a `1` could
          only arrive from a caller contradicting itself. Rendering nothing
          then beats printing "1 packages". */}
      {counted && (
        <p id={captionId} className="text-xs text-muted-foreground">
          {packageCount} packages
        </p>
      )}
    </div>
  );
}
